import { useReactiveVar } from "@apollo/client";
import { correctOutOfBoundsPageSelection, deleteAllNotifications } from "@flixbus-phx/marketplace-common";
import * as React from "react";
import getNumberForPageSize from "../../../../shared/helpers/getNumberForPageSize/getNumberForPageSize";
import getRideFilterInput from "../../../../shared/helpers/getRideFilterInput/getRideFilterInput";
import { PageSize } from "../../../../shared/types/schema";
import { removeAllRideUpdateErrors } from "../../../../state/rideUpdateErrors/handleRideUpdateErrors";
import rideUpdateErrorsVar from "../../../../state/rideUpdateErrors/reactiveVariable";
import usePagination, { paginationVar } from "../../../../state/usePagination/usePagination";
import useRideFilter, { rideFilterVar } from "../../../../state/useRideFilter/useRideFilter";
import useRideSort, { rideSortVar } from "../../../../state/useRideSort/useRideSort";
import { useFilterRidesQuery } from "../../api/operations.generated";
import getRideUpdateErrorsPerRide from "../../helpers/getRideUpdateErrorsPerRide/getRideUpdateErrorsPerRide";
import RideItemInfoText from "../../ui/rideItemInfoText/RideItemInfoText";
import RideTableBodySkeleton from "../../ui/rideTableBodySkeleton/RideTableBodySkeleton";
import { MemoizedRideTableRow } from "../rideTableRow/RideTableRow";

const RideTableBody: React.FC = () => {
  const [rideFilter] = useRideFilter(rideFilterVar);
  const [rideSort] = useRideSort(rideSortVar);
  const [pagination, setPagination] = usePagination(paginationVar);

  const rideUpdateErrors = useReactiveVar(rideUpdateErrorsVar);

  React.useEffect(() => {
    return () => {
      deleteAllNotifications();
      removeAllRideUpdateErrors();
    };
  }, []);

  const { data, loading, error } = useFilterRidesQuery({
    variables: {
      pageRequest: pagination,
      rideFilter: getRideFilterInput(rideFilter, false, false),
      sort: rideSort,
    },
    pollInterval: 1000 * 120,
    fetchPolicy: "cache-first",
  });

  React.useEffect(() => {
    if (data?.filterRides) {
      correctOutOfBoundsPageSelection(
        getNumberForPageSize(pagination.pageSize),
        data.filterRides.total,
        pagination,
        (newPagination) => setPagination({ page: newPagination.page, pageSize: newPagination.pageSize as PageSize })
      );
    }
  }, [data]);

  if (loading) {
    return <RideTableBodySkeleton />;
  }

  if (error) {
    return <RideItemInfoText isError />;
  }

  if (data?.filterRides) {
    if (data.filterRides.items.length > 0) {
      return (
        <>
          {data.filterRides.items.map((ride) => {
            return (
              <MemoizedRideTableRow
                ride={ride}
                key={ride.id}
                rideUpdateErrors={getRideUpdateErrorsPerRide(rideUpdateErrors, ride.id)}
              />
            );
          })}
        </>
      );
    }
    return <RideItemInfoText />;
  }

  return <></>;
};

export default RideTableBody;
